import { FC, Fragment, Suspense, useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { useLazyLoadQuery } from 'react-relay';
import { StudentReport, DismissedStudent, mergeDemonstrated, isStudentDismissed } from './helper/chartHelper';
import CloserLookStudentRow from '../../CloserLook/CloserLookStudentRow';
import Paginator, { PAGE_SIZE } from '../../CloserLook/Paginator';
import TakingTooLongSection from '../../CloserLook/TakingTooLongSection';
import { getDemonstratedSkillsByStudent } from '../../../graphQLOperations/Diagnostics';
import { ThreeDotLoader } from '../../Fallback/ThreeDotsLoader';
import { DiagnosticsDemonstratedSkillsQuery } from '../../../graphQLOperations/__generated__/DiagnosticsDemonstratedSkillsQuery.graphql';

interface Props {
  visible: boolean;
  onHide: () => void;
  tooLongStudents: Student[];
  tooFastStudents: Student[];
  tooFastCount: number;
  dismissedStudents: DismissedStudent[];
  onDismiss: (report: StudentReport) => void;
  initialTab: 0 | 1;
}

type Student = StudentReport;
type FastStudentRowsWithSkillsProps = {
  students: Student[];
  dismissedStudents: DismissedStudent[];
  onConfirmDialogToggle: (open: boolean) => void;
  onDismiss: (report: StudentReport) => void;
};

/**
 * The "finished quickly" rows for the current page. `studentsDemonstratedSkillsReport`
 * takes the whole page's `studentIds` at once, so this suspends a single time for the
 * page (rather than once per row) and then merges the counts back onto each student.
 */
const FastStudentRowsWithSkills: FC<FastStudentRowsWithSkillsProps> = ({
  students,
  dismissedStudents,
  onConfirmDialogToggle,
  onDismiss,
}) => {
  const data = useLazyLoadQuery<DiagnosticsDemonstratedSkillsQuery>(
    getDemonstratedSkillsByStudent,
    {
      filter: {
        studentIds: students.map(s => s.student.id),
      },
    },
    { fetchPolicy: 'network-only' },
  );

  const enrichedStudents = mergeDemonstrated(students, data.studentsDemonstratedSkillsReport);

  return (
    <Fragment>
      {enrichedStudents.map(student => (
        <CloserLookStudentRow
          key={student.student.id}
          student={student}
          variant="too-fast"
          borderColorClass="tw-border-x-0 tw-border-t-0 !tw-border-b-amber-100"
          demonstratedSkills={student.demonstrated}
          isDismissed={isStudentDismissed(student, dismissedStudents)}
          onDismiss={onDismiss}
          onConfirmDialogToggle={onConfirmDialogToggle}
        />
      ))}
    </Fragment>
  );
};

const StudentsCloserLookModal: FC<Props> = ({
  visible,
  onHide,
  tooLongStudents,
  tooFastStudents,
  tooFastCount,
  dismissedStudents,
  onDismiss,
  initialTab,
}) => {
  const [activeIndex, setActiveIndex] = useState(initialTab);
  const [fastPage, setFastPage] = useState(0);

  const [openConfirmCount, setOpenConfirmCount] = useState(0);
  const handleConfirmDialogToggle = (open: boolean) =>
    setOpenConfirmCount(count => Math.max(0, count + (open ? 1 : -1)));
  const anyConfirmOpen = openConfirmCount > 0;

  const fastPageStudents = tooFastStudents.slice(fastPage * PAGE_SIZE, (fastPage + 1) * PAGE_SIZE);
  const totalFastPages = Math.ceil(tooFastStudents.length / PAGE_SIZE);

  useEffect(() => {
    setActiveIndex(initialTab);
    setFastPage(0);
  }, [initialTab, visible]);

  return (
    <Dialog
      header={
        <span className="tw-font-atkinson tw-text-neutral-950 tw-font-bold tw-text-xl">
          Students who need a closer look
        </span>
      }
      visible={visible}
      onHide={onHide}
      className="tw-w-[630px] tw-h-[600px]"
      maskClassName={`tw-transition-opacity ${anyConfirmOpen ? 'tw-opacity-0 tw-pointer-events-none' : ''}`}
      footer={<Button link label="Close" className="tw-ml-auto" onClick={onHide} />}
    >
      <TabView
        className="nc_diagnostic-modal-tab-view nc_diagnostic-closer-look-tab-view"
        activeIndex={activeIndex}
        onTabChange={e => setActiveIndex(e.index as 0 | 1)}
      >
        <TabPanel
          className={`nc_diagnostic-modal-tab-panel ${activeIndex === 0 ? 'active' : ''}`}
          disabled={tooLongStudents.length === 0}
          header={
            <>
              Taking too long{' '}
              <span className="tw-rounded-lg tw-text-xs tw-font-bold tw-px-[6px] tw-py-[4px]">
                {tooLongStudents.length}
              </span>
            </>
          }
        >
          <TakingTooLongSection students={tooLongStudents} onConfirmDialogToggle={handleConfirmDialogToggle} />
        </TabPanel>

        <TabPanel
          className={`nc_diagnostic-modal-tab-panel ${activeIndex === 1 ? 'active' : ''}`}
          disabled={tooFastStudents.length === 0}
          header={
            <>
              Finished quickly{' '}
              <span className="tw-rounded-lg tw-text-xs tw-font-bold tw-px-[6px] tw-py-[4px]">{tooFastCount}</span>
            </>
          }
        >
          <div className="tw-rounded-xl tw-border-2 tw-border-solid tw-border-neutral-100 tw-p-3">
            <span className="tw-text-sm tw-font-atkinson tw-text-neutral-950">
              <span className="tw-font-bold">Check to confirm these results fit their understanding.</span>
              <br></br>
              If they don&apos;t, you can reset their diagnostic. Resetting wipes their results and starts the
              diagnostic over.
            </span>
          </div>

          <div className="nc_diagnostic-modal-table">
            <div className="tw-flex tw-items-center tw-gap-2 tw-py-1 tw-px-3 tw-border-x-0 tw-border-t-0 !tw-border-b-amber-100 tw-border-b tw-border-solid">
              <span className="tw-flex-1" />
              <span className="tw-text-xs tw-font-atkinson tw-text-neutral-500 tw-uppercase tw-w-20 tw-text-center tw-font-semibold">
                Skills Demonstrated
              </span>
              <span className="tw-text-xs tw-font-atkinson tw-text-neutral-500 tw-uppercase tw-w-16 tw-text-center tw-font-semibold">
                Time Spent
              </span>
              <span className="tw-w-44 tw-shrink-0" />
            </div>
            <Suspense fallback={<ThreeDotLoader dark />}>
              <FastStudentRowsWithSkills
                students={fastPageStudents}
                dismissedStudents={dismissedStudents}
                onConfirmDialogToggle={handleConfirmDialogToggle}
                onDismiss={onDismiss}
              />
            </Suspense>
          </div>

          <Paginator
            page={fastPage}
            total={tooFastCount}
            totalPages={totalFastPages}
            onChange={setFastPage}
            label="Finished quickly"
          />
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default StudentsCloserLookModal;
